import {isAsyncThunkAction} from '@reduxjs/toolkit'

import {getArrivalGuaranteeValidate} from '@web/actions/deliveryInfo'
import {fetchWaitingState} from '@web/actions/waiting'
import {
    DeliveryNotOnSaleReasons,
    ErrorMessage,
    ErrorType,
    GiftSendCodes,
    OrderFailureType,
    ReasonTypeType,
    ReloadNotAllowedReason,
    ValidationFailureCodes,
} from '@web/core/constants'
import {PAY_M, PC_PAY, getGiftSendUrl} from '@web/core/urls'
import {GwError} from '@web/helpers/api'
import {getQuery, examineUrlSafety} from '@web/helpers/path'
import authCreator from '@web/middlewares/auth'
import {orderInterrupted, OrderInterruptType} from '@web/middlewares/orderInterrupted'

import {initOrderSheetByAPI} from '../../../actions'
import {CheckoutRootState as RootState} from '../../../interfaces'
import {getResultUrl} from '../../../utils/urlHelper'

/** 주문서 로딩되기 전 액션들 모음 */
const isInitActions = isAsyncThunkAction(fetchWaitingState, initOrderSheetByAPI)

const isValidationFailureCheckSkipActions = isAsyncThunkAction(getArrivalGuaranteeValidate)

const authMiddleware = authCreator<RootState>({
    validationFailureCodes: [...ValidationFailureCodes, ...GiftSendCodes],
    /** App fail case Handler */
    validationFailureHandler: ({next, action, state}) => {
        const code = action.payload?.code ?? ''
        const query = getQuery<{backUrl: string; 'nl-au': string}>()
        const backUrl = state.common.backUrl || examineUrlSafety(query?.backUrl, '')

        // 선물하기 주문서 > 선물 받을 사람 정보가 없을 경우 친구 선택으로 Redirect.
        if (GiftSendCodes.includes(code)) {
            const friendsSessionKey = (function () {
                if (action.payload?.giftFailure?.friendsSessionKey) {
                    // blank 예시 : null, “ “, “    “
                    return action.payload.giftFailure.friendsSessionKey.trim()
                }
                return null
            })()
            return next(
                orderInterrupted([
                    {
                        type: 'REDIRECT',
                        url: getGiftSendUrl({
                            actionUID: query['nl-au'],
                            orderSheetId: action.payload?.giftFailure?.orderSheetId ?? '',
                            availableCount: action.payload?.giftFailure?.availableGiftingPeopleCount ?? 0,
                            friendsSessionKey,
                        }),
                    },
                ]),
            )
        }

        if (code === ErrorType.ETIMEDOUT_FAILED) {
            return next(
                orderInterrupted([
                    {
                        type: 'ALERT',
                        message:
                            action?.payload?.fallbackMessage ??
                            '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.',
                    },
                    backUrl ? {type: 'REDIRECT', url: backUrl} : {type: 'BACK', shouldClose: true},
                ]),
            )
        }
        if (!isValidationFailureCheckSkipActions(action) && action.payload?.validationFailure?.reasonType) {
            const validationFailure = action.payload.validationFailure
            const {reasonType} = validationFailure
            if ([...ReloadNotAllowedReason, ...DeliveryNotOnSaleReasons].includes(reasonType as ReasonTypeType)) {
                const defaultMessages = ReloadNotAllowedReason.includes(reasonType as ReasonTypeType)
                    ? ErrorMessage.OUT_OF_STOCK
                    : '상품의 배송정보가 변경되었습니다. 상품을 다시 확인하시고 구매해주세요.'
                return next(
                    orderInterrupted([
                        {
                            type: 'ALERT',
                            message: validationFailure.fallbackMessage ?? defaultMessages,
                        },
                        backUrl ? {type: 'REDIRECT', url: backUrl} : {type: 'BACK', shouldClose: true},
                    ]),
                )
            }
            if (reasonType === OrderFailureType.ORDER_ALREADY_CREATED) {
                const orderNo = action.payload?.validationFailure?.reasonAttributes.orderNo
                const {baseUrl} = getResultUrl({orderNo})
                const OrderAlreadyCreatedAlert: OrderInterruptType[] = isInitActions(action)
                    ? [
                          {
                              type: 'ALERT',
                              message: '이미 결제가 완료된 주문입니다. 주문완료 화면으로 이동합니다',
                          },
                      ]
                    : []
                return next(
                    orderInterrupted([
                        ...OrderAlreadyCreatedAlert,
                        {
                            type: 'REDIRECT',
                            url: baseUrl,
                        },
                    ]),
                )
            }
        }
        return next(action)
    },
    /** App 이용정지 case */
    transactionStopHandler: ({next, action, isMobileDisplay}) => {
        const reason = (function () {
            try {
                const error = JSON.parse(action.error.message) as GwError
                return error.data.message
            } catch {
                return null
            }
        })()

        if (!reason) {
            return next(action)
        }

        if (isInitActions(action)) {
            const url = isMobileDisplay ? `${PAY_M}/mobile/memberStop` : `${PAY_M}/memberStop`
            return next(
                orderInterrupted([
                    {
                        type: 'REDIRECT',
                        url,
                        reason,
                    },
                ]),
            )
        } else {
            if (isMobileDisplay) {
                return next(
                    orderInterrupted([
                        {
                            type: 'ALERT',
                            message:
                                '회원님은 네이버페이 서비스 이용이 중지되었습니다. 관련 문의 사항은 네이버페이 고객센터로 문의하세요.',
                            reason,
                        },
                    ]),
                )
            } else {
                return next(
                    orderInterrupted([
                        {
                            type: 'POPUP',
                            props: [`${PC_PAY}/memberStopPopup`, 'stopPopup', 'width=520,height=600,alwaysRaised=yes'],
                            reason,
                        },
                    ]),
                )
            }
        }
    },
})

/** 페이 회원 인증 미들웨어  */
export default authMiddleware
